import { createSelector } from '@reduxjs/toolkit';
import { isDevEnv, isTestEnv } from '../../config';
import { MemberInfo, selectMember } from '../../profile';
import { RootState } from '../../store';

export const getRoomState = (state: RootState) => state.room;

export const selectRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.room
);

export const selectRoomMemberError = createSelector(
  [getRoomState],
  (roomState) => roomState.roomMemberError
);

export const selectSuggestedRoomsError = createSelector(
  [getRoomState],
  (roomState) => roomState.suggestedRoomsError
);

export const selectLoadingRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingRoom
);

export const selectLoadingCreateSubgroup = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingCreateSubgroup
);

export const selectRoomError = createSelector(
  [getRoomState],
  (roomState) => roomState.roomError
);

export const selectLoadingSuggestedRooms = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingSuggestedRooms
);

export const selectMemberRooms = createSelector(
  [getRoomState],
  (roomState) => roomState.memberRooms
);

export const selectMyRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.myRoomSubgroups
);

export const selectLoadingMyRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingMyRoomSubgroups
);

export const selectOtherRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.otherRoomSubgroups
);

export const selectLoadingOtherRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingOtherRoomSubgroups
);

export const selectModeratorRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.moderatorRoomSubgroups
);

export const selectLoadingModeratorRoomSubgroups = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingModeratorRoomSubgroups
);

export const selectModeratedRooms = createSelector(
  [getRoomState],
  (roomState) => {
    return roomState.moderatedRooms;
  }
);

export const selectMemberRoomsCount = createSelector(
  [getRoomState],
  (roomState) => roomState.memberRooms.length
);

export const selectLoadingMemberRooms = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingMemberRooms
);

export const selectRoomsByTopic = (topic: string, isHomePage?: boolean) =>
  createSelector([getRoomState], (roomState) => {
    const topicRooms = roomState.roomsByTopic[topic]?.data || [];

    if (!isHomePage) return topicRooms;

    const topicRoomsNotJoined = topicRooms.filter(
      (topicRoom) =>
        !roomState.memberRooms.some((room) => room.id === topicRoom.id)
    );

    return topicRoomsNotJoined;
  });

export const selectLoadingRoomsByTopic = (topic: string) =>
  createSelector([getRoomState], (roomState) =>
    Boolean(roomState.loadingRoomsByTopic[topic])
  );

export const selectRoomFindResults = createSelector(
  [getRoomState],
  (roomState) => roomState.roomsSearchResult
);

export const selectLoadingRoomSearchResults = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingSearchRooms
);

export const selectLoadingJoinRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingJoinRoom
);

export const selectLoadingLeaveRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingLeaveRoom
);

export const selectSuggestedRooms = createSelector(
  [getRoomState],
  (roomState) => roomState.suggestedRooms.data
);

export const selectSuggestedRoomsContinuationToken = createSelector(
  [getRoomState],
  (roomState) => roomState.suggestedRooms?.xMsContinuationToken
);

export const selectIsRoomsListView = createSelector(
  [getRoomState],
  (roomState) => roomState.isRoomsListView
);

export const selectPeopleInTheRoom = (companions: MemberInfo[]) =>
  createSelector([getRoomState, selectMember], (roomState, member) =>
    roomState.peopleInTheRoom.data.filter(
      (people) =>
        (isDevEnv() || isTestEnv() || people.memberDisplayName) &&
        people.memberId !== member?.id &&
        !companions.find((companion) => companion.memberId === people.memberId)
    )
  );

export const selectLoadingPeopleInTheRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingPeopleInTheRoom
);

export const selectPeopleInTheRoomContinuationToken = createSelector(
  [getRoomState],
  (roomState) => roomState.peopleInTheRoom?.xMsContinuationToken
);

export const selectLinkedRooms = createSelector(
  [getRoomState],
  (roomState) => roomState.linkedRooms
);

export const selectLinkedRoomsContinuationToken = createSelector(
  [getRoomState],
  (roomState) => roomState.linkedRooms?.xMsContinuationToken
);

export const selectHasMorePeopleInTheRoom = createSelector(
  [getRoomState],
  (roomState) => Boolean(roomState.peopleInTheRoom?.xMsContinuationToken)
);

export const selectPeopleInTheRoomCount = createSelector(
  [getRoomState],
  (roomState) => roomState.peopleInTheRoomCount
);

export const selectLoadingPeopleInTheRoomWidget = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingPeopleInTheRoomWidget
);

export const selectPreviousStoryDestination = createSelector(
  [getRoomState],
  (roomState) => roomState.previousStoryDestination
);

export const selectLoadingPrivateRoomCode = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingPrivateRoomCode
);

export const selectModeratorsInTheRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.moderatorsInTheRoom.data
);

export const selectModeratorsInTheRoomContinuationToken = createSelector(
  [getRoomState],
  (roomState) => roomState.moderatorsInTheRoom.xMsContinuationToken
);

export const selectLoadingModeratorsInTheRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingModeratorsInTheRoom
);

export const selectModeratorsInTheRoomCount = createSelector(
  [getRoomState],
  (roomState) => roomState.moderatorsInTheRoomCount
);

export const selectSelectedContentItem = createSelector(
  [getRoomState],
  (roomState) => roomState.selectedContentItem
);

export const selectFeaturedContent = createSelector(
  [getRoomState],
  (roomState) => roomState.featuredContent
);

export const selectLoadingContentForCarousel = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingContent
);

export const selectCurrentMemberInRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.currentMemberInRoom
);

/**
 * Member display name scoped to the exact room. Without a linked room identity the API
 * returns the main account, whose platform-wide handle is never a room name.
 */
export const selectRoomScopedDisplayName = (state: RootState, roomId: string | undefined) => {
  const roomState = getRoomState(state);
  const roomMember = roomState.currentMemberInRoom;
  const mainMemberId = selectMember(state)?.id;
  return roomId && roomState.currentMemberInRoomId === roomId
    && roomMember?.id && mainMemberId && roomMember.id !== mainMemberId
    ? roomMember.memberDisplayName?.trim() || undefined
    : undefined;
};

export const selectLoadingCurrentMemberInRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingCurrentMemberInRoom
);

export const selectSharePrivateRoomCode = createSelector(
  [getRoomState],
  (roomState) => roomState.sharePrivateRoomCode
);

export const selectLoadingSharePrivateRoomCode = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingSharePrivateRoomCode
);

export const selectSharePrivateRoomCodeError = createSelector(
  [getRoomState],
  (roomState) => roomState.sharePrivateRoomCodeError
);

export const selectLoadingSubmitConsentToJoinRoom = createSelector(
  [getRoomState],
  (roomState) => roomState.loadingSubmitConsentToJoinRoom
);
